import logger from '@/layers/support/monitoring/logger';
import { SavedSearch } from '@/types/models';
import { fetchWrapper } from '../utils/fetchWrapper';

const LOG_MODULE = 'FE [Presentation / Services / searches]';

const API_URL = '/api/programs/searches';

const ENABLE_CLIENT_CACHE = process.env.NODE_ENV !== 'development';

type CacheEntry<T> = {
  value: T;
  expiresAtMs: number;
};

const SAVED_SEARCHES_TTL_MS = 2 * 60 * 1000; // 2 minutes
const SAVED_SEARCH_BY_ID_TTL_MS = 10 * 60 * 1000; // 10 minutes

const savedSearchesCache = new Map<string, CacheEntry<SavedSearch[]>>();
const savedSearchesInFlight = new Map<string, Promise<SavedSearch[]>>();

const savedSearchByIdCache = new Map<number, CacheEntry<SavedSearch>>();
const savedSearchByIdInFlight = new Map<number, Promise<SavedSearch>>();

function nowMs() {
  return Date.now();
}

function getFreshCacheValue<T>(entry?: CacheEntry<T>) {
  if (!entry) return null;
  if (entry.expiresAtMs <= nowMs()) return null;
  return entry.value;
}

function getSavedSearchesKey(userId: string, patientId?: string) {
  return `${userId}::${patientId ?? ''}`;
}

function invalidateSavedSearchesCache(userId?: string, patientId?: string) {
  if (!userId) {
    savedSearchesCache.clear();
    savedSearchesInFlight.clear();
    return;
  }
  savedSearchesCache.delete(getSavedSearchesKey(userId, patientId));
  savedSearchesInFlight.delete(getSavedSearchesKey(userId, patientId));
}

export const fetchSavedSearches = async (
  userId: string,
  patientId?: string,
): Promise<SavedSearch[]> => {
  logger.debug(`${LOG_MODULE} fetchSavedSearches: begin`, { userId, patientId });

  try {
    const cacheKey = getSavedSearchesKey(userId, patientId);
    if (ENABLE_CLIENT_CACHE) {
      const cached = getFreshCacheValue(savedSearchesCache.get(cacheKey));
      if (cached) {
        logger.debug(`${LOG_MODULE} fetchSavedSearches: cache hit`, {
          userId,
          patientId,
          count: cached.length,
        });
        return cached;
      }
    }

    const existingInFlight = savedSearchesInFlight.get(cacheKey);
    if (existingInFlight) {
      logger.debug(`${LOG_MODULE} fetchSavedSearches: dedupe in-flight`, { userId, patientId });
      return await existingInFlight;
    }

    // NOTE: the server scopes the read to the authenticated user (claims.sub) and
    // ignores any `userId` query param, so we don't send it on the wire. `userId`
    // is still used above purely as the client-side cache key (it equals the
    // caller's own Cognito sub).
    const queryParams = new URLSearchParams();
    if (patientId) queryParams.append('patientId', patientId);

    logger.debug(`${LOG_MODULE} fetchSavedSearches: queryParams`, { queryParams });

    const qs = queryParams.toString();
    const url = qs ? `${API_URL}?${qs}` : API_URL;
    const requestPromise = fetchWrapper
      .get<SavedSearch[]>(url)
      .then(result => {
        if (ENABLE_CLIENT_CACHE) {
          savedSearchesCache.set(cacheKey, {
            value: result,
            expiresAtMs: nowMs() + SAVED_SEARCHES_TTL_MS,
          });
        }
        return result;
      })
      .finally(() => {
        savedSearchesInFlight.delete(cacheKey);
      });

    savedSearchesInFlight.set(cacheKey, requestPromise);
    const result = await requestPromise;

    logger.debug(`${LOG_MODULE} fetchSavedSearches: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} fetchSavedSearches: error`, { error });
    throw error;
  }
};

export const fetchSavedSearchById = async (id: number): Promise<SavedSearch> => {
  logger.debug(`${LOG_MODULE} fetchSavedSearchById: begin`, { id });

  try {
    if (ENABLE_CLIENT_CACHE) {
      const cached = getFreshCacheValue(savedSearchByIdCache.get(id));
      if (cached) {
        logger.debug(`${LOG_MODULE} fetchSavedSearchById: cache hit`, { id });
        return cached;
      }
    }

    const existingInFlight = savedSearchByIdInFlight.get(id);
    if (existingInFlight) {
      logger.debug(`${LOG_MODULE} fetchSavedSearchById: dedupe in-flight`, { id });
      return await existingInFlight;
    }

    const requestPromise = fetchWrapper
      .get<SavedSearch>(`${API_URL}/${id}`)
      .then(result => {
        if (ENABLE_CLIENT_CACHE) {
          savedSearchByIdCache.set(id, {
            value: result,
            expiresAtMs: nowMs() + SAVED_SEARCH_BY_ID_TTL_MS,
          });
        }
        return result;
      })
      .finally(() => {
        savedSearchByIdInFlight.delete(id);
      });

    savedSearchByIdInFlight.set(id, requestPromise);
    const result = await requestPromise;

    logger.debug(`${LOG_MODULE} fetchSavedSearchById: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} fetchSavedSearchById: error`, { error });
    throw error;
  }
};

export const createSavedSearch = async (search: Partial<SavedSearch>): Promise<SavedSearch> => {
  logger.debug(`${LOG_MODULE} createSavedSearch: begin`, { search });

  try {
    const result = await fetchWrapper.post<SavedSearch>(API_URL, search);
    if (ENABLE_CLIENT_CACHE) {
      invalidateSavedSearchesCache(search.user_id);
    }

    logger.debug(`${LOG_MODULE} createSavedSearch: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} createSavedSearch: error`, { error });
    throw error;
  }
};

export const updateSavedSearch = async (
  id: number,
  search: Partial<SavedSearch>,
): Promise<SavedSearch> => {
  logger.debug(`${LOG_MODULE} updateSavedSearch: begin`, { id, search });

  try {
    const result = await fetchWrapper.put<SavedSearch>(`${API_URL}/${id}`, search);
    if (ENABLE_CLIENT_CACHE) {
      savedSearchByIdCache.delete(id);
      invalidateSavedSearchesCache(search.user_id);
    }

    logger.debug(`${LOG_MODULE} updateSavedSearch: end`, { result });
    return result;
  } catch (error) {
    logger.error(`${LOG_MODULE} updateSavedSearch: error`, { error });
    throw error;
  }
};
